'use client';

import { Button } from '@magicschool/spellbook/components/Button';
import { CollapsiblePrimitive } from '@magicschool/spellbook/components/Collapsible';
import { DropdownMenu } from '@magicschool/spellbook/components/DropdownMenu';
import { Icon, type IconName } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Text } from '@magicschool/spellbook/components/Text';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useFieldSizingFallback } from '@magicschool/spellbook/hooks/useFieldSizingFallback';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { applySlots, cn } from '@magicschool/spellbook/lib/utils';
import { Children, type ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { useTypingAnimation } from './useTypingAnimation';

export const ChatInputLeadingAdornmentSlot = Slot<React.HTMLAttributes<HTMLDivElement>>({
  displayName: 'ChatInput.LeadingAdornment',
  renderDefault: ({ className, ...props }) => (
    <div data-spellbook-name="chat-input-leading-adornment" className={cn('flex-shrink-0', className)} {...props} />
  ),
});

export type ChatInputTextareaProps = React.ComponentProps<'textarea'> & {
  /** @internal Injected by ChatInput root via useSlots. */
  animatedPlaceholder?: {
    prompts: string[];
    ariaDescription?: string;
    onChange?: (text: string, index: number) => void;
  };
};

export const ChatInputTextareaSlot = Slot<ChatInputTextareaProps>({
  displayName: 'ChatInput.Textarea',
  isRenderedByDefault: true,
  renderDefault: ({
    className,
    style,
    ref: externalRef,
    value,
    onChange,
    onFocus,
    onBlur,
    animatedPlaceholder,
    'aria-label': ariaLabel = 'Message',
    'aria-describedby': consumerDescribedBy,
    ...props
  }: ChatInputTextareaProps & { ref?: React.Ref<HTMLTextAreaElement> }) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(externalRef, () => internalRef.current!);
    useFieldSizingFallback(internalRef, { value });

    const ariaId = useId();
    const [isFocused, setIsFocused] = useState(false);
    const [trackedHasContent, setTrackedHasContent] = useState(false);
    const hasContent = value != null ? String(value).trim().length > 0 : trackedHasContent;
    const animationActive = !!animatedPlaceholder?.prompts.length && !isFocused && !hasContent;
    const animatedText = useTypingAnimation(animationActive, animatedPlaceholder?.prompts ?? [], animatedPlaceholder?.onChange);

    return (
      <div data-spellbook-name="chat-input-textarea" className="relative overflow-hidden">
        {animationActive && (
          <div
            aria-hidden="true"
            className={cn(
              'absolute inset-0 -translate-x-px flex items-end pointer-events-none text-start text-sm leading-normal text-placeholder motion-reduce:hidden',
            )}
          >
            <span className="truncate min-w-0">{animatedText}</span>
            <span className="inline-block w-px h-[1.2em] ml-px shrink-0 bg-placeholder motion-safe:[animation:chat-cursor_0.9s_step-end_infinite]" />
          </div>
        )}
        <textarea
          ref={internalRef}
          rows={1}
          value={value}
          onChange={(e) => {
            if (value === undefined) setTrackedHasContent(e.target.value.trim().length > 0);
            onChange?.(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            if (value === undefined) setTrackedHasContent(e.target.value.trim().length > 0); // re-sync for uncontrolled
            setIsFocused(false);
            onBlur?.(e);
          }}
          className={cn(
            'w-full block bg-transparent resize-none focus:outline-none p-0',
            // overflow-x-hidden: Safari can size the content wider than the box, and its scrollbar then covers the one line.
            'text-sm leading-normal field-sizing-content min-h-[1lh] max-h-[3lh] overflow-x-hidden overflow-y-auto @md:max-h-[6lh]',
            'text-default-text placeholder:text-placeholder',
            'disabled:cursor-not-allowed disabled:text-disabled-foreground',
            animationActive && 'motion-safe:placeholder:opacity-0',
            className,
          )}
          aria-label={ariaLabel}
          // Joined, not replaced: a consumer description must not drop the suggestions one.
          aria-describedby={[animatedPlaceholder ? ariaId : null, consumerDescribedBy].filter(Boolean).join(' ') || undefined}
          style={style}
          {...props}
        />
        {animatedPlaceholder && (
          <span id={ariaId} className="sr-only">
            {animatedPlaceholder.ariaDescription ?? animatedPlaceholder.prompts.slice(0, 3).join(', ')}
          </span>
        )}
      </div>
    );
  },
});

type ChatInputAttachmentsCollapsibleProps = Pick<
  React.ComponentProps<typeof CollapsiblePrimitive.Root>,
  'open' | 'defaultOpen' | 'onOpenChange' | 'disabled'
>;

export interface ChatInputAttachmentsSlotProps extends ChatInputAttachmentsCollapsibleProps {
  /** Pre-formatted label for the disclosure trigger (e.g. `"3 files"`). Consumer owns pluralization and i18n. */
  label: string;
  /** Optional content rendered to the right of the trigger pill (e.g. an error tooltip). */
  trailing?: ReactNode;
  /** Localized sr-only labels for the trigger. */
  text?: {
    /** @default `"Show attachments"` */
    show?: string;
    /** @default `"Hide attachments"` */
    hide?: string;
  };
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const ChatInputAttachmentsSlot = Slot<ChatInputAttachmentsSlotProps>({
  displayName: 'ChatInput.Attachments',
  renderDefault: ({ label, defaultOpen = true, open, onOpenChange, trailing, text: textProp, children, className, ...rest }) => {
    if (!children || Children.count(children) === 0) return null;
    return (
      <ChatInputAttachmentsCollapsible
        data-spellbook-name="chat-input-attachments"
        label={label}
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={onOpenChange}
        trailing={trailing}
        text={textProp}
        className={className}
        {...rest}
      >
        {children}
      </ChatInputAttachmentsCollapsible>
    );
  },
});

function ChatInputAttachmentsCollapsible({
  label,
  defaultOpen,
  open,
  onOpenChange,
  trailing,
  text: textProp,
  children,
  className,
  ...rest
}: ChatInputAttachmentsSlotProps & { defaultOpen: boolean }) {
  const text = { show: 'Show attachments', hide: 'Hide attachments', ...textProp };
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = isControlled ? (open as boolean) : internalOpen;
  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <CollapsiblePrimitive.Root className={cn('px-3 pb-1 w-full', className)} open={isOpen} onOpenChange={handleOpenChange} {...rest}>
      <div className="flex items-center gap-1">
        <CollapsiblePrimitive.Trigger asChild>
          <Button size="sm" color="neutral" variant="ghost" aria-label={isOpen ? text.hide : text.show}>
            <Button.LeadingIcon name={isOpen ? 'chevron-up' : 'chevron-down'} />
            {label}
          </Button>
        </CollapsiblePrimitive.Trigger>
        {trailing}
      </div>
      <CollapsiblePrimitive.Content className="data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close">
        <div className="flex flex-wrap gap-3 pt-2">{children}</div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}

export interface ChatInputMenuSlotProps {
  children?: ReactNode;
  disabled?: boolean;
  /** Icon name for the trigger button. @default `"plus"` */
  icon?: IconName;
  /** Localizable strings for the menu trigger. */
  text?: {
    /** Accessible label for the trigger button. @default `"Add attachment"` */
    label?: string;
    /** Visible tooltip shown on hover. When set, the tooltip becomes the accessible name so `label` is not needed separately. */
    tooltip?: string;
  };
  /** Called when the menu closes before focus is returned to the trigger. Prevent default to redirect focus elsewhere. */
  onCloseAutoFocus?: (event: Event) => void;
  'data-testid'?: string;
}

const ChatInputMenuSlotBase = Slot<ChatInputMenuSlotProps>({
  displayName: 'ChatInput.Menu',
  renderDefault: ({ children, disabled, icon = 'plus', text, onCloseAutoFocus, 'data-testid': testId }) => {
    const label = text?.label ?? 'Add attachment';
    const tooltip = text?.tooltip;
    const button = (
      <IconButton
        data-spellbook-name="chat-input-menu"
        name={icon}
        variant="ghost"
        color="neutral"
        size="sm"
        label={tooltip ? undefined : label}
        disabled={disabled}
        data-testid={testId}
      />
    );
    return (
      <DropdownMenu>
        {tooltip ? (
          <DropdownMenu.Tooltip mode="label">
            <DropdownMenu.Trigger asChild>{button}</DropdownMenu.Trigger>
            <DropdownMenu.TooltipContent>{tooltip}</DropdownMenu.TooltipContent>
          </DropdownMenu.Tooltip>
        ) : (
          <DropdownMenu.Trigger asChild>{button}</DropdownMenu.Trigger>
        )}
        <DropdownMenu.Content onCloseAutoFocus={onCloseAutoFocus}>{children}</DropdownMenu.Content>
      </DropdownMenu>
    );
  },
});

export const ChatInputMenuSlot = applySlots(ChatInputMenuSlotBase, DropdownMenu);

/**
 * A row of context chips at the very top of the card, above the textarea.
 *
 * Distinct from `ToolbarStart`, which carries chips for settings the turn will run WITH — web
 * search, a knowledge base — down in the toolbar. This is for what the turn is ABOUT: the thing
 * the message will act on, which the writer needs in view while composing rather than tucked
 * beneath the field they are typing in.
 *
 * Distinct from `Attachments` too: that slot is file intake, with disclosure chrome and a count.
 */
export const ChatInputContextSlot = Slot<React.HTMLAttributes<HTMLDivElement>>({
  displayName: 'ChatInput.Context',
  renderDefault: ({ className, ...props }) => (
    <div
      data-spellbook-name="chat-input-context"
      className={cn('flex flex-wrap items-center gap-1.5 px-3 pt-3 @md:px-4 @md:pt-4', className)}
      {...props}
    />
  ),
});

export const ChatInputToolbarStartSlot = Slot<React.HTMLAttributes<HTMLDivElement>>({
  displayName: 'ChatInput.ToolbarStart',
  renderDefault: ({ className, ...props }) => (
    <div data-spellbook-name="chat-input-toolbar-start" className={cn('flex items-center gap-2', className)} {...props} />
  ),
});

export const ChatInputToolbarEndSlot = Slot<React.HTMLAttributes<HTMLDivElement>>({
  displayName: 'ChatInput.ToolbarEnd',
  renderDefault: ({ className, ...props }) => (
    <div data-spellbook-name="chat-input-toolbar-end" className={cn('flex items-center gap-2', className)} {...props} />
  ),
});

export interface ChatInputSendButtonSlotProps {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** @default "Send" */
  label?: string;
  /** @internal Injected by ChatInput root — true while the root `disabled` prop is set (e.g. AI streaming). */
  rootDisabled?: boolean;
  /**
   * Optional rich tooltip content. When set, the tooltip shows this content (e.g. a send-limit
   * explanation) instead of the label, and the label becomes an sr-only accessible name on the
   * button itself. When omitted, the label is shown as a hover tooltip via `mode="label"`.
   */
  tooltip?: React.ReactNode;
  /** Forwarded to the underlying send button element (e.g. for test selectors). */
  'data-testid'?: string;
}

function ChatInputSendButtonDefault({
  disabled,
  rootDisabled,
  onClick,
  label = 'Send',
  tooltip,
  'data-testid': dataTestId,
}: ChatInputSendButtonSlotProps) {
  const sendButton = (
    <IconButton
      data-spellbook-name="chat-input-send-button"
      name="arrow-up"
      animate="hover"
      size="sm"
      shape="circle"
      disabled={disabled || rootDisabled}
      onClick={onClick}
      data-testid={dataTestId}
      {...(tooltip ? { label } : {})}
    />
  );

  return (
    <Tooltip align="center" mode={tooltip ? 'description' : 'label'}>
      <Tooltip.Content>{tooltip || label}</Tooltip.Content>
      <Tooltip.Trigger>{sendButton}</Tooltip.Trigger>
    </Tooltip>
  );
}

export const ChatInputSendButtonSlot = Slot<ChatInputSendButtonSlotProps>({
  displayName: 'ChatInput.SendButton',
  renderDefault: (props) => <ChatInputSendButtonDefault {...props} />,
  isRenderedByDefault: true,
});

export interface ChatInputStopButtonSlotProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** @default "Stop" */
  label?: string;
  /** When set, shown as tooltip and `label` is sr-only; otherwise `label` is the tooltip. */
  tooltip?: React.ReactNode;
  'data-testid'?: string;
}

/** Stays clickable when ChatInput root is disabled for streaming. */
function ChatInputStopButtonDefault({ onClick, label = 'Stop', tooltip, 'data-testid': dataTestId }: ChatInputStopButtonSlotProps) {
  const stopButton = (
    <IconButton
      data-spellbook-name="chat-input-stop-button"
      name="square"
      size="sm"
      shape="circle"
      variant="outlined"
      color="neutral"
      // 12px icon so the square fits inside the circle.
      className="border-default-icon hover:border-default-icon active:border-default-icon data-[state=open]:border-default-icon [&_svg]:fill-current [&_svg]:size-3 [&_svg]:min-w-3 [&_svg]:min-h-3"
      onClick={onClick}
      data-testid={dataTestId}
      {...(tooltip ? { label } : {})}
    />
  );

  return (
    <Tooltip align="center" mode={tooltip ? 'description' : 'label'}>
      <Tooltip.Content>{tooltip || label}</Tooltip.Content>
      <Tooltip.Trigger>{stopButton}</Tooltip.Trigger>
    </Tooltip>
  );
}

export const ChatInputStopButtonSlot = Slot<ChatInputStopButtonSlotProps>({
  displayName: 'ChatInput.StopButton',
  renderDefault: (props) => <ChatInputStopButtonDefault {...props} />,
});

export interface ChatInputFootnoteSlotProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Validation error to surface beneath the card. When set, takes precedence over
   * `children` and renders with error styling + `role="alert"`. ChatInput's drop
   * pipeline injects rejection messages here automatically.
   */
  error?: string | null;
  /** Called when the user clicks the dismiss button on an error footnote. */
  onErrorDismiss?: () => void;
}

/**
 * Helper / legal disclaimer text rendered below the card. Also acts as the validation
 * surface when ChatInput's `fileDrop` rejects a drop — the rejection message replaces
 * `children` with error styling so consumers don't have to choose between the two.
 */
export const ChatInputFootnoteSlot = Slot<ChatInputFootnoteSlotProps>({
  displayName: 'ChatInput.Footnote',
  renderDefault: ({ error, onErrorDismiss, className, children, ...props }) => {
    if (!error && !children) return null;
    if (error) {
      return (
        <div
          data-spellbook-name="chat-input-footnote"
          role="alert"
          className={cn('flex items-center justify-center gap-1.5 mt-2', className)}
          {...props}
        >
          <Icon name="alert-circle" size="sm" color="error" aria-hidden="true" />
          <Text variant="caption-1" element="p" color="error">
            {error}
          </Text>
          {onErrorDismiss && (
            <IconButton
              data-spellbook-name="chat-input-footnote-dismiss"
              name="x"
              size="sm"
              variant="ghost"
              color="error"
              label="Dismiss"
              onClick={onErrorDismiss}
            />
          )}
        </div>
      );
    }
    return (
      <div data-spellbook-name="chat-input-footnote" className={cn('mt-1 @md:mt-2 text-center text-pretty text-xs', className)} {...props}>
        <Text variant="caption-1" color="default-soft">
          {children}
        </Text>
      </div>
    );
  },
});

export interface ChatInputDropOverlaySlotProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether a drag is currently over the card. Provided automatically by ChatInput. */
  isDragActive?: boolean;
  /** Whether the dragged files match the accepted types. Provided automatically by ChatInput. */
  isDragAccept?: boolean;
  /** Whether the dragged files do not match accepted types. Provided automatically by ChatInput. */
  isDragReject?: boolean;
  /** Localized text. Falls back to English defaults. */
  text?: {
    /** @default "Drop files to attach" */
    accept?: string;
    /** @default "This file type isn't accepted" */
    reject?: string;
  };
}

/**
 * Absolute-positioned overlay shown over the card while a drag is in progress.
 * Only rendered when ChatInput is configured with `fileDrop` and a drag is active.
 */
export const ChatInputDropOverlaySlot = Slot<ChatInputDropOverlaySlotProps>({
  displayName: 'ChatInput.DropOverlay',
  renderDefault: ({ isDragActive, isDragAccept: _isDragAccept, isDragReject, text, className, children, ...props }) => {
    if (!isDragActive) return null;
    const message = isDragReject ? (text?.reject ?? "This file type isn't accepted") : (text?.accept ?? 'Drop files to attach');
    return (
      <div
        data-spellbook-name="chat-input-drop-overlay"
        aria-hidden="true"
        className={cn(
          'absolute inset-0 z-10 flex items-center justify-center rounded-lg pointer-events-none',
          'border-2 border-dashed',
          isDragReject ? 'border-error-stroke bg-error-muted-background/80' : 'border-primary-stroke bg-primary-muted-background/80',
          'backdrop-blur-sm transition-colors duration-150',
          className,
        )}
        {...props}
      >
        {children ?? (
          <Text variant="body-1" color={isDragReject ? 'error' : 'default'} className="font-semibold">
            {message}
          </Text>
        )}
      </div>
    );
  },
});

export interface ChatInputMenuItemProps {
  /** Lucide icon name displayed before the label. */
  icon: IconName;
  /** Visible label text. */
  label: string;
  /** Optional description shown below the label. */
  description?: string;
  onSelect?: () => void;
  disabled?: boolean;
}

export function ChatInputMenuItem({ icon, label, description, onSelect, disabled }: ChatInputMenuItemProps) {
  return (
    <DropdownMenu.Item data-spellbook-name="chat-input-menu-item" onSelect={onSelect} disabled={disabled}>
      <DropdownMenu.ItemLeadingIcon name={icon} />
      <DropdownMenu.ItemLabel>{label}</DropdownMenu.ItemLabel>
      {description && <DropdownMenu.ItemDescription>{description}</DropdownMenu.ItemDescription>}
    </DropdownMenu.Item>
  );
}
ChatInputMenuItem.displayName = 'ChatInput.MenuItem';
